import { type SchoolExtended } from '@/interfaces/schoolInterface';

let ready = false;
const tasks = [] as HubspotFormCreateTask[];
let hsCreate: (task: HubspotFormCreateTask) => void;

const processCreate = (task: HubspotFormCreateTask) => {
	hsCreate(task);
};

const init = () => {
	const script = document.createElement('script');
	script.src = 'https://js.hsforms.net/forms/v2.js';
	document.body.appendChild(script);

	script.addEventListener('load', () => {
		hsCreate = window.hbspt.forms.create;
		ready = true;

		tasks.forEach((tasks) => {
			processCreate(tasks);
		});
	});
};

export const hubspot = {
	init,
	create(task: HubspotFormCreateTask) {
		if (!ready) {
			tasks.push(task);
			return;
		}

		processCreate(task);
	},
};

interface HubspotFormCreateTask {
	portalId: string;
	formId: string;
	target: string;
	onFormReady?: (form: HTMLElement) => void;
	onFormSubmitted: () => void;
}

export interface HubspotPrefillField {
	/** HubSpot property internal name, e.g. 'email' or 'school_linker'. */
	name: string;
	value: string;
	/** Hide the field once populated (default true). Only hides when the value is non-empty. */
	hide?: boolean;
}

/**
 * Submit a form as the school's champion rather than as the logged-in user.
 *
 * For a form that writes a *company* property, who submits it decides where the value
 * lands: HubSpot writes company properties to whichever company the submitting contact is
 * associated with. A champion's own submission lands on their school; an EF's lands on
 * Real Schools, silently and on our own record. Pass this as `prefill` on such a form when
 * staff are acting for a school — it overrides the component's identity fields, since
 * `prefillHubspotForm` applies fields in order and the component puts `prefill` last.
 *
 * Returns nothing when the school has no champion account. There is then nobody to submit
 * as, and falling back to the staff member's own details is the bug this exists to stop —
 * so the caller should say so instead of offering a form. The backend does not count the
 * generic school-wide login as a champion, so "no champion" includes a school that has
 * only that.
 *
 * These fields stay visible (`hide: false`), unlike the identity fields they override.
 * Prefilled-and-hidden is fine when the form is filled with your own details; filling it
 * with somebody else's and hiding whose they are is not.
 */
export const schoolChampionPrefill = (school: SchoolExtended | null): HubspotPrefillField[] => {
	if (!school?.championEmail) {
		return [];
	}

	return [
		{ name: 'email', value: school.championEmail, hide: false },
		{ name: 'firstname', value: school.championFirstName, hide: false },
		{ name: 'lastname', value: school.championLastName, hide: false },
	];
};

/** Identifies the injected stylesheet, so it's only added once per document. */
const formStyleId = 'rs-hubspot-form-styles';

/**
 * Resolve one of our brand colours from the host document.
 *
 * The injected stylesheet can't reference `var(--rs-color-*)` — those are declared on the
 * host `:root`, and the form lives in its own document — so read the value out here rather
 * than restating brand colours as literals that would drift from variables.scss.
 */
const brandColour = (name: string, fallback: string): string => {
	const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
	return value ? `rgb(${value})` : fallback;
};

/**
 * Styles for the form's own document.
 *
 * **Hiding prefilled fields** — hides what we've populated, and puts a field straight back
 * the moment HubSpot marks it invalid. Without that, a rejected value (HubSpot requires the
 * email domain to have an MX or A record) blocks submission with its error message hidden,
 * and the form just looks broken. CSS rather than JavaScript so it reacts the instant
 * HubSpot adds the error markup, with no polling and no race against their validation. The
 * `:has()` selectors outrank the plain one, so the un-hide always beats the hide.
 *
 * **The submit button** — mirrors `.btn.btn-primary.btn-large` from forms.scss, since a
 * prefilled form is often nothing but this button and HubSpot's default styling looks
 * nothing like the rest of the portal. Roboto Flex is imported again here for the same
 * reason the colours are read from the host: the form's document has its own font set.
 *
 * Sizing is `min-height` plus horizontal padding only — an `<input type="submit">` centres
 * its own label vertically, so there's no need to reproduce the flex centring that
 * `.btn` uses (and no magic padding to keep in step with the height).
 *
 * Lengths here must be absolute, never `rem`: typography.scss sets the host root to 62.5%
 * so `1rem` is 10px in the app, but the form's document keeps the browser default of 16px,
 * and a `rem` value would silently come out over half again too big.
 *
 * There's no submitting state: HubSpot doesn't touch the button while the submission is in
 * flight — no `disabled`, no class, no markup change at all — so there's nothing to style.
 *
 * `!important` throughout because HubSpot's own stylesheet loads after this one.
 */
const buildFormStyles = (): string => `
	@import url('https://fonts.googleapis.com/css2?family=Roboto+Flex:opsz,wght@8..144,600&display=swap');

	.hs-form-field.hs-prefilled-hidden {
		display: none !important;
	}

	.hs-form-field.hs-prefilled-hidden:has(.hs-error-msgs li),
	.hs-form-field.hs-prefilled-hidden:has(input.invalid) {
		display: block !important;
	}

	.hs-button,
	input[type='submit'] {
		display: inline-block !important;
		transition: filter 0.25s !important;
		cursor: pointer !important;
		border: 1px solid ${brandColour('--rs-color-brand-honey', 'rgb(248 141 42)')} !important;
		border-radius: 4px !important;
		background-color: ${brandColour('--rs-color-brand-honey', 'rgb(248 141 42)')} !important;
		padding: 0 40px !important;
		min-height: 60px !important;
		color: ${brandColour('--rs-color-brand-white', 'rgb(255 255 255)')} !important;
		font-weight: 600 !important;
		font-size: 30px !important;
		font-family: 'Roboto Flex', sans-serif !important;
		line-height: 1 !important;
		letter-spacing: 1px !important;
		text-transform: uppercase !important;
		text-decoration: none !important;
	}

	.hs-button:hover,
	input[type='submit']:hover {
		filter: brightness(115%) !important;
	}
`;

/**
 * Add our stylesheet to the document the form actually lives in.
 *
 * HubSpot renders the embed inside an iframe, which our own stylesheets can't reach — so
 * the rules have to go in there with it. HubSpot writes that iframe from the host page, so
 * it's same-origin and we can style it directly. Harmless if a form ever renders inline
 * instead: then this is just the host document.
 */
const ensureFormStyles = (form: HTMLElement): void => {
	const doc = form.ownerDocument;
	const parent = doc.head ?? doc.body;
	if (!parent || doc.getElementById(formStyleId)) {
		return;
	}

	const style = doc.createElement('style');
	style.id = formStyleId;
	style.textContent = buildFormStyles();
	parent.appendChild(style);
};

/**
 * Prefill fields on an embedded HubSpot form. Call from within `onFormReady`, passing
 * the form element it hands you. Field names are matched by suffix, so this works
 * whether or not HubSpot prefixes them with the CRM object type (e.g. `0-1/email` for
 * a contact field or `0-2/some_property` for a company field).
 */
export const prefillHubspotForm = (form: HTMLElement, fields: HubspotPrefillField[]): void => {
	ensureFormStyles(form);

	fields.forEach(({ name, value, hide = true }) => {
		const input = form.querySelector<HTMLInputElement>(`input[name="${name}"], input[name$="/${name}"]`);
		if (!input) {
			return;
		}

		input.value = value;
		input.dispatchEvent(new Event('input', { bubbles: true }));

		// Hide the field's own wrapper once it's populated, so prefilled fields don't
		// clutter the form. Only when there's actually a value, so empties stay editable.
		// A class rather than an inline style, so `prefillStyles` can reveal the field
		// again if HubSpot rejects the value.
		//
		// Toggled rather than added, because a later field overriding an earlier one has to
		// be able to override its visibility too — a caller passing `hide: false` over the
		// component's own identity fields wants them shown, and adding-only would leave
		// them hidden by the earlier pass.
		input.closest<HTMLElement>('.hs-form-field')?.classList.toggle('hs-prefilled-hidden', hide && !!value);
	});
};
